'use client';

import { LinkList, LinkListItem, Picture, PrimaryNav, TextLink } from '@petsmart-ui/sparky';
import type { MouseEvent } from 'react';

import { dataLayerPush, getBaseUrlJoined, trackMainNavLinkClick } from '@libs/analytics';
import { usePageLayout } from '@libs/common-next';
import { handleServiceClick } from '@libs/experience-api';

import { type CategoryTypes, type RecursiveMenuProps } from './types';
import { getCategoryPath, trackCategoryPath, trackingData } from './utils';

export function RecursiveMenu({ parentCategory, parentCategories, path }: RecursiveMenuProps) {
    const { isAuthenticated, isSoftAuthenticated } = usePageLayout();

    return parentCategories
        ?.filter(
            (category, index, self) =>
                // TODO: should be remove if we can get the category stack to work properly with unique IDs
                index === self.findIndex(c => c.sysId === category.sysId)
        )
        .map((category: CategoryTypes) => {
            const {
                sysId,
                type = 'text',
                displayText,
                shopAllCategory,
                link,
                imageUrl,
                categories,
                wrapListItems,
            } = category;

            const categoryPath = getCategoryPath(path, displayText);

            function handleOnClick(event: MouseEvent<Element, globalThis.MouseEvent>) {
                handleServiceClick({
                    authType: isAuthenticated,
                    event,
                    link,
                    isSoftAuthenticated,
                });

                trackMainNavLinkClick(trackCategoryPath(categoryPath));

                dataLayerPush(
                    trackingData({
                        selection: `${parentCategory} | ${displayText}`,
                        link_url: getBaseUrlJoined(link),
                    })
                );
            }

            if (!categories?.length) {
                if (shopAllCategory) {
                    return null;
                }

                if (type === 'image') {
                    return (
                        <PrimaryNav.Item key={`${sysId}-${parentCategory}`} className={`category__item--${type}`}>
                            <TextLink
                                href={link}
                                onClick={event => handleOnClick(event)}
                                title={displayText}
                                id={displayText}
                            >
                                <Picture
                                    imgSrc={imageUrl ?? ''}
                                    imgAlt={displayText ?? ''}
                                    imageComponentProps={{
                                        loading: 'lazy',
                                    }}
                                />
                                <span className="sparky-u-is-vishidden">{displayText}</span>
                            </TextLink>
                        </PrimaryNav.Item>
                    );
                }

                return (
                    <PrimaryNav.Item key={`${sysId}-${parentCategory}`} className={`category__item--${type}`}>
                        <TextLink
                            href={link}
                            variant="secondary"
                            onClick={event => handleOnClick(event)}
                            id={displayText}
                        >
                            {displayText}
                        </TextLink>
                    </PrimaryNav.Item>
                );
            }

            return (
                <PrimaryNav.Item key={`${sysId}-${parentCategory}`} className={`category__list--${type}`}>
                    <LinkList className={wrapListItems ? 'is-legacy-grid' : ''}>
                        <LinkListItem onClick={event => handleOnClick(event)} href={link} text={displayText} />

                        <RecursiveMenu
                            parentCategory={`${parentCategory} | ${displayText}`}
                            parentCategories={categories}
                            path={categoryPath}
                            wrapListItems={Boolean(wrapListItems)}
                        />
                    </LinkList>
                </PrimaryNav.Item>
            );
        });
}
